import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { twMerge } from 'tailwind-merge';
import { CloseButton } from '@repo/common';
import EvidenceUploadCard from './EvidenceUploadCard';
import StatusPill from './StatusPill';
import { uploadToS3 } from '../../../../../../api/jobboard/apply.api';
import {
  getPathfinderEvidenceScorePreview,
  getPathfinderEvidenceUploadUrl,
} from '../../../../../../api/pathfinder/pathfinder.api';
import { ReactComponent as ArrowLeftIcon } from '@repo/common/assets/icons/20px/icon_20px_arrow-left.svg';
import { ReactComponent as CancelIcon } from '@repo/common/assets/icons/20px/icon_20px_cancel.svg';
import { ReactComponent as CheckCircleFilledIcon } from '@repo/common/assets/icons/24px/icon_24px_check-circle_filled.svg';
import { ReactComponent as CheckCircleIcon } from '@repo/common/assets/icons/20px/icon_20px_check-circle_outline.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';
import { ReactComponent as DragIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as IdeaIcon } from '@repo/common/assets/icons/16px/icon_16px_idea_outline.svg';
import { ReactComponent as LinkIcon } from '@repo/common/assets/icons/20px/icon_20px_links.svg';
import { ReactComponent as NavigationRightIcon } from '@repo/common/assets/icons/16px/icon_16px_navigation-right.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as ResetIcon } from '@repo/common/assets/icons/20px/icon_20px_reset.svg';
import { ReactComponent as UploadIcon } from '@repo/common/assets/icons/16px/icon_16px_upload.svg';

const MAX_FILE_SIZE = 50 * 1024 * 1024;
const SAVE_DELAY_MS = 850;
const FIGMA_DOCUMENT_GUIDE_TEXT =
  "Provide a clear, full-colour scan of a valid passport, driver's licence, or national identity card. All four corners must be visible, no details obscured. Expired documents are not accepted.";

const LINK_TYPES = [
  'Link',
  'Portfolio',
  'Personal Website',
  'LinkedIn',
  'GitHub',
  'Behance',
  'Dribbble',
  'Notion',
  'Blog',
  'Instagram',
  'TikTok',
  'Threads',
  'X (Twitter)',
  'YouTube',
];

const DEFAULT_EVIDENCE_ITEMS = [
  {
    id: 'visa-copy',
    title: 'Visa Copy',
    status: 'required',
    icon: 'file',
    category: 'Visa',
    points: 0,
    variableName: 'V_CODE_EVIDENCE',
    targetCondition: 'L2/C2',
    guide:
      'Upload a clear copy of your current visa so your eligibility can be checked against your visa status.',
    state: 'default',
  },
  {
    id: 'degree-certificate',
    title: 'Degree Certificate',
    status: 'required',
    icon: 'file',
    category: 'Education',
    points: 0,
    variableName: 'D_LEVEL',
    targetCondition: 'L1/C4',
    scoreRule:
      'Uploading an official degree certificate confirms your reported education level and raises the C4 score.',
    guide:
      "Upload your official degree certificate (bachelor's, master's, or associate's degree) to verify your highest education level.",
    state: 'default',
  },
  {
    id: 'degree-certificate-kr',
    title: 'Degree Certificate KR',
    status: 'required',
    icon: 'file',
    category: 'Education',
    points: 0,
    variableName: 'K_DEGREE_KR',
    targetCondition: 'L1/C2, C4',
    guide:
      'Upload a Korean degree certificate or official Korean education document if applicable.',
    state: 'default',
  },
  {
    id: 'institute-certificate-kr',
    title: 'Institute Certificate KR',
    status: 'required',
    icon: 'file',
    category: 'Education',
    points: 0,
    variableName: 'ACA_LANG_CERT',
    targetCondition: 'L1/C3',
    guide:
      'Upload an official Korean institute certificate to verify your language or academic training record.',
    state: 'default',
  },
  {
    id: 'topik',
    title: 'TOPIK',
    status: 'required',
    icon: 'file',
    category: 'Language',
    points: 0,
    variableName: 'TOPIK_LEVEL',
    targetCondition: 'C3 (Korean communication competency)',
    scoreRule:
      'Even if a user self-reports TOPIK level 5 in L2, the score can be capped by proxy rules without evidence. Once the official TOPIK report is uploaded and approved, the level is confirmed as L1_Fact and can receive a verified tag such as IS_TOPIK_VERIFIED = true.',
    guide:
      'Upload your official TOPIK score report so your claimed Korean proficiency can be verified.',
    state: 'default',
  },
  {
    id: 'tool-outputs',
    title: 'Tool Outputs',
    status: 'required',
    icon: 'file',
    category: 'Work Output',
    points: 0,
    variableName: 'TOOL_RESULT_EVIDENCE',
    targetCondition: 'O2 (tool usage)',
    scoreRule: 'TOOL_RESULT_EVIDENCE = true makes the O2 score rise to 4.',
    guide:
      'Upload practical tool outputs that you created yourself using tools you selected, such as Figma, GA4, code, source files, or screenshots.',
    state: 'default',
  },
  {
    id: 'career-certificates',
    title: 'Career Certificates',
    status: 'required',
    icon: 'file',
    category: 'Career',
    points: 0,
    variableName: 'CAREER_CERT_EVIDENCE or HAS_CAREER_CERT',
    targetCondition: 'C5 (practical work experience)',
    scoreRule:
      'Used to officially verify the self-reported TOTAL_RELEVANT_MONTHS value for relevant career months.',
    guide:
      'Upload career certificates or employment documents that verify your relevant months of experience.',
    state: 'default',
  },
  {
    id: 'portfolio-kr',
    title: 'Portfolio KR',
    status: 'required',
    icon: 'file',
    category: 'Portfolio',
    points: 0,
    variableName: 'KR_PORTFOLIO',
    targetCondition: 'C5 (practical work experience)',
    scoreRule: 'KR_PORTFOLIO = true makes the C5 score rise to 4.',
    guide:
      'Upload a Korean portfolio that demonstrates practical work experience relevant to your target role.',
    state: 'default',
  },
  {
    id: 'awards',
    title: 'Awards',
    status: 'required',
    icon: 'file',
    category: 'Awards',
    points: 0,
    variableName: 'AWARD_COUNT',
    targetCondition: 'C5 (trust evidence)',
    scoreRule:
      'If a user reports 2 or more awards in the survey, C5 receives a proxy cap of 3. Uploaded award certificates can add a trust badge for B2B review.',
    guide:
      'Upload award certificates or proof documents so your reported awards can be verified.',
    state: 'default',
  },
  {
    id: 'project-result',
    title: 'Project Result',
    status: 'required',
    icon: 'file',
    category: 'Project',
    points: 0,
    variableName: 'HAS_RESULT_EVIDENCE',
    targetCondition: 'O3 (execution), O4 (problem solving)',
    scoreRule:
      'HAS_RESULT_EVIDENCE = true satisfies the base requirement for O3 and O4 level 4. For O4, this variable alone keeps the score at 3; combining it with KPI evidence can raise it to 4.',
    guide:
      'Upload proof of project results that can verify your execution and problem-solving evidence.',
    state: 'default',
  },
  {
    id: 'kpi',
    title: 'KPI',
    status: 'optional',
    icon: 'graph',
    category: 'Performance',
    points: 0,
    variableName: 'HAS_KPI_EVIDENCE',
    targetCondition: 'O3 (execution), O4 (problem solving)',
    scoreRule:
      'HAS_RESULT_EVIDENCE = true and HAS_KPI_EVIDENCE = true make O3 and O4 rise to 4 together.',
    guide:
      'Upload KPI evidence or add a link that shows measurable project outcomes.',
    state: 'default',
    allowLinks: true,
    links: [],
  },
];

const PATHFINDER_DRAFT_STORAGE_KEY = 'pathfinderDraft';
const DEGREE_LEVEL_CODES = new Set([
  'EXP_ASSOCIATE',
  'EXP_BACHELOR',
  'EXP_MASTER',
]);
const CAREER_EXPERIENCE_CODES = new Set([
  'INTERNSHIP',
  'PRACTICUM',
  'PROJECT_BASED',
  'CONTRACT',
  'FULLTIME',
]);
const PROJECT_RESULT_CODES = new Set(['KPI_OPT_RESULT', 'KPI_OPT_EXPLAIN']);
const KPI_RESULT_CODES = new Set(['KPI_OPT_NUMERIC', 'KPI_OPT_KPI']);

const readPathfinderDraft = () => {
  if (typeof window === 'undefined') return null;

  try {
    const draft = JSON.parse(
      window.localStorage.getItem(PATHFINDER_DRAFT_STORAGE_KEY) || 'null',
    );
    return draft && typeof draft === 'object' ? draft : null;
  } catch {
    return null;
  }
};

const makeAnswerReader = (draft = {}) => {
  const { answers = {}, sectionAnswers = {} } = draft || {};

  return (questionCode) => {
    if (answers[questionCode] !== undefined) return answers[questionCode];

    return Object.values(sectionAnswers).reduce(
      (found, section) =>
        found !== undefined || !section || typeof section !== 'object'
          ? found
          : section[questionCode],
      undefined,
    );
  };
};

const toArray = (value) =>
  Array.isArray(value) ? value : value ? [value] : [];
const hasAny = (value, codes) => toArray(value).some((item) => codes.has(item));

const shouldRequestEvidenceItem = (item, readAnswer) => {
  const goalType = readAnswer('Q_GOAL_TYPE');
  const visa = readAnswer('Q_GOAL_DETAIL');
  const expBlock = readAnswer('Q_EXP_BLOCK') || {};
  const languageRows = toArray(
    readAnswer('Q_LANG_SCORE') || readAnswer('Q_LANGUAGE_SCORE'),
  );
  const kpiEvidence = readAnswer('Q_KPI_EVIDENCE');

  switch (item.id) {
    case 'visa-copy':
      return Boolean(visa && visa !== 'NO_VISA');
    case 'degree-certificate':
      return DEGREE_LEVEL_CODES.has(expBlock.step1);
    case 'degree-certificate-kr':
      return readAnswer('Q_KR_DEGREE') === 'KR_DEG_YES';
    case 'institute-certificate-kr':
      return (
        ['GOAL_UG', 'GOAL_GRAD', 'GOAL_TRANSFER'].includes(goalType) &&
        languageRows.some(
          (row) =>
            row?.certType &&
            !['LANG_NONE', 'LANG_TOPIK1'].includes(row.certType),
        )
      );
    case 'topik':
      return languageRows.some(
        (row) => row?.certType === 'LANG_TOPIK1' && row?.level,
      );
    case 'tool-outputs':
      return toArray(readAnswer('Q_TOOL_USAGE')).length > 0;
    case 'career-certificates':
      return toArray(expBlock.step2).some((code) =>
        CAREER_EXPERIENCE_CODES.has(code),
      );
    case 'portfolio-kr':
      return readAnswer('Q_PORTFOLIO_AWARD') === 'PORT_YES';
    case 'awards':
      return ['PLANG_1', 'PLANG_2'].includes(readAnswer('Q_PORTFOLIO_LANG'));
    case 'project-result':
      return hasAny(kpiEvidence, PROJECT_RESULT_CODES);
    case 'kpi':
      return hasAny(kpiEvidence, KPI_RESULT_CODES);
    default:
      return false;
  }
};

const getRequestedEvidenceItems = (draft) => {
  const readAnswer = makeAnswerReader(draft);
  return DEFAULT_EVIDENCE_ITEMS.filter((item) =>
    shouldRequestEvidenceItem(item, readAnswer),
  ).map((item) => ({
    ...item,
    state: 'default',
    fileName: undefined,
    links: item.allowLinks ? [] : item.links,
  }));
};

const getRequestedEvidenceItemsFromPathfinderDraft = () =>
  getRequestedEvidenceItems(readPathfinderDraft());

const buildDraftFromSessionData = (sessionData) => {
  if (!sessionData) return null;
  const sections = {
    C1: sessionData.c1Data || {},
    C2: sessionData.c2Data || {},
    C3: sessionData.c3Data || {},
    C4: sessionData.c4Data || {},
    C5: sessionData.c5Data || {},
  };
  const overlayData = sessionData.overlayData || {};
  const answers = Object.values(sections).reduce(
    (acc, section) =>
      section && typeof section === 'object' ? { ...acc, ...section } : acc,
    { ...overlayData },
  );
  return { answers, sectionAnswers: sections };
};

const getRequestedEvidenceItemsWithFallback = (sessionData) => {
  const localDraft = readPathfinderDraft();
  const hasLocalAnswers =
    localDraft && Object.keys(localDraft.answers || {}).length > 0;
  if (hasLocalAnswers) return getRequestedEvidenceItems(localDraft);
  return getRequestedEvidenceItems(buildDraftFromSessionData(sessionData));
};

const STATUS_PRIORITY = {
  success: 3,
  failed: 2,
  uploading: 1,
};

const ERROR_MESSAGES = {
  size: 'File exceeds the 50MB limit. Please upload a smaller file.',
  network: 'Upload interrupted. Please try again.',
  generic: 'Upload failed. Please try again.',
};

const makeLinkRow = () => ({
  id: Date.now() + Math.random(),
  linkType: 'Link',
  linkUrl: '',
});

const normalizeFiles = (item) => item.files || [];
const normalizeLinks = (item) => item.links || [];
const hasValidLinks = (links) => links.some((link) => link.linkUrl?.trim());
const hasSuccessfulFiles = (files) =>
  files.some((file) => file.status === 'success');
const isEvidenceUploaded = (item) => item.state === 'uploaded';
const getFileSignature = (file) =>
  [
    file.persistedId || '',
    file.key || '',
    file.fileName || file.file?.name || '',
    file.file?.size || file.fileSize || '',
    file.status || '',
    file.errorType || '',
  ].join(':');
const getFilesSignature = (files = []) =>
  files.map(getFileSignature).sort().join('|');
const getLinksSignature = (links = []) =>
  links
    .filter((link) => link.linkUrl?.trim() || link.linkType !== 'Link')
    .map((link) => `${link.linkType || 'Link'}:${link.linkUrl?.trim() || ''}`)
    .sort()
    .join('|');
const toFiniteNumber = (value, fallback = 0) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};
const clampScorePercent = (value) =>
  Math.max(0, Math.min(100, toFiniteNumber(value)));
const mergeEvidenceFiles = (existingFiles = [], incomingFiles = []) => {
  if (!incomingFiles.length) return existingFiles;

  const incomingKeys = new Set(
    incomingFiles
      .map((file) => file.persistedId || file.key || file.id)
      .filter(Boolean),
  );
  const incomingSignatures = new Set(
    incomingFiles.map(
      (file) =>
        `${file.fileName || file.file?.name || ''}:${file.file?.size || ''}`,
    ),
  );
  const localFiles = existingFiles.filter((file) => {
    const key = file.persistedId || file.key || file.id;
    const signature = `${file.fileName || file.file?.name || ''}:${file.file?.size || ''}`;
    const isLocal =
      !file.persistedId && (file.status !== 'success' || file.key);
    return (
      isLocal && !incomingKeys.has(key) && !incomingSignatures.has(signature)
    );
  });

  return [...incomingFiles, ...localFiles];
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
};

function DotsLoader() {
  return (
    <span className='flex items-center h-6 gap-1' aria-label='Saving'>
      <span className='rounded-full size-2 bg-grayscale-gray00' />
      <span className='size-1.5 rounded-full bg-grayscale-gray00' />
      <span className='rounded-full size-1 bg-grayscale-gray00' />
    </span>
  );
}

function FileUploadRow({ fileData, onDelete, onRetry }) {
  if (!fileData) return null;

  const file = fileData.file;
  const extension =
    file.name?.split('.').pop()?.toUpperCase() ||
    file.type?.split('/')?.pop()?.toUpperCase() ||
    'FILE';
  const isFailed = fileData.status === 'failed';
  const isUploading = fileData.status === 'uploading';
  const progress = Math.min(fileData.progress || 0, 100);

  return (
    <div
      className={twMerge(
        'flex w-full flex-col gap-4 rounded-lg border bg-grayscale-gray00 p-4',
        isFailed ? 'border-functional-failed03' : 'border-grayscale-gray02',
      )}
    >
      <div className='flex items-center w-full gap-3'>
        <div className='flex size-[42px] shrink-0 items-center justify-center rounded border border-grayscale-gray02 bg-grayscale-gray01'>
          <span className='max-w-full px-1 truncate typo-b12Strong text-text-placeholder'>
            {extension}
          </span>
        </div>
        <div className='flex flex-col flex-1 min-w-0 gap-1'>
          <div className='truncate typo-b14Strong text-text-primary'>
            {file.name}
          </div>
          {isFailed ? (
            <div className='typo-b13 text-text-failed'>
              {ERROR_MESSAGES[fileData.errorType] || ERROR_MESSAGES.generic}
            </div>
          ) : isUploading ? (
            <div className='typo-b13 text-text-placeholder'>
              {formatFileSize(fileData.loaded || 0)} of{' '}
              {formatFileSize(file.size)}
            </div>
          ) : (
            <div className='typo-b13 text-text-placeholder'>
              {formatFileSize(file.size)}
            </div>
          )}
        </div>
        {isFailed ? (
          <div className='flex items-center gap-3 shrink-0'>
            <button type='button' aria-label='Retry upload' onClick={onRetry}>
              <ResetIcon className='size-5 text-icon-secondary' />
            </button>
            <button type='button' aria-label='Remove file' onClick={onDelete}>
              <CancelIcon className='size-5 text-icon-secondary' />
            </button>
          </div>
        ) : isUploading ? (
          <button
            type='button'
            className='shrink-0'
            aria-label='Cancel upload'
            onClick={onDelete}
          >
            <CancelIcon className='size-5 text-icon-secondary' />
          </button>
        ) : (
          <button
            type='button'
            className='shrink-0'
            aria-label='Delete file'
            onClick={onDelete}
          >
            <DeleteIcon className='size-5 text-icon-secondary' />
          </button>
        )}
      </div>
      {isUploading && (
        <div className='h-[5px] w-full overflow-hidden rounded-full bg-grayscale-gray02'>
          <div
            className='h-full rounded-full bg-green-deep02 transition-[width] duration-300'
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
    </div>
  );
}

function LinkRows({ links, onChange }) {
  const [openId, setOpenId] = useState(null);

  return (
    <div className='flex flex-col w-full gap-2'>
      {links.map((link) => (
        <div key={link.id} className='flex items-center w-full gap-2'>
          <div className='flex h-10 w-[30px] shrink-0 items-center justify-center text-icon-placeholder'>
            <DragIcon className='h-[33px] w-[33px]' />
          </div>
          <div className='relative w-[170px] shrink-0'>
            <button
              type='button'
              className='flex items-center justify-between w-full h-10 px-3 border rounded-lg border-grayscale-gray02 bg-grayscale-gray01 typo-b13Strong text-text-secondary'
              onClick={() =>
                setOpenId((value) => (value === link.id ? null : link.id))
              }
            >
              <span className='truncate'>{link.linkType}</span>
              <span className='text-icon-placeholder'>v</span>
            </button>
            {openId === link.id && (
              <div className='absolute left-0 top-11 z-20 max-h-[220px] w-full overflow-y-auto rounded-lg border border-grayscale-gray02 bg-grayscale-gray00 py-1 shadow-[0px_8px_24px_rgba(23,20,16,0.12)]'>
                {LINK_TYPES.map((type) => (
                  <button
                    key={type}
                    type='button'
                    className='flex items-center w-full px-3 text-left h-9 typo-b13 text-text-secondary hover:bg-grayscale-gray01'
                    onClick={() => {
                      onChange((prev) =>
                        prev.map((item) =>
                          item.id === link.id
                            ? { ...item, linkType: type }
                            : item,
                        ),
                      );
                      setOpenId(null);
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className='relative flex-1 min-w-0'>
            <LinkIcon className='absolute -translate-y-1/2 pointer-events-none left-3 top-1/2 size-5 text-icon-placeholder' />
            <input
              className='w-full h-10 pl-10 pr-3 border rounded-lg outline-none border-grayscale-gray02 bg-grayscale-gray00 typo-b13 text-text-primary placeholder:text-text-placeholder focus:border-green-deep02'
              placeholder='Please enter the link'
              value={link.linkUrl}
              onChange={(event) =>
                onChange((prev) =>
                  prev.map((item) =>
                    item.id === link.id
                      ? { ...item, linkUrl: event.target.value }
                      : item,
                  ),
                )
              }
            />
          </div>
          <button
            type='button'
            className='flex h-10 w-[30px] shrink-0 items-center justify-center rounded-lg text-icon-placeholder hover:bg-grayscale-gray01'
            aria-label='Delete link'
            onClick={() =>
              onChange((prev) => prev.filter((item) => item.id !== link.id))
            }
          >
            <DeleteIcon className='size-5' />
          </button>
        </div>
      ))}
      <button
        type='button'
        className='flex h-9 w-full items-center justify-center rounded-lg bg-grayscale-gray01 text-icon-secondary hover:bg-grayscale-gray02 active:scale-[0.99]'
        aria-label='Add link'
        onClick={() => onChange((prev) => [...prev, makeLinkRow()])}
      >
        <PlusIcon className='size-5' />
      </button>
    </div>
  );
}

function InfoPanel({ title, icon, children, warning = false }) {
  return (
    <section
      className={twMerge(
        'w-full shrink-0 overflow-hidden rounded-[15px] border',
        warning ? 'border-functional-warning01' : 'border-grayscale-gray02',
      )}
    >
      <div
        className={twMerge(
          'flex h-9 items-center gap-2 px-4',
          warning
            ? 'bg-functional-warning01'
            : 'border-b border-grayscale-gray02 bg-grayscale-gray01',
        )}
      >
        {icon}
        <span
          className={twMerge(
            'typo-b12Strong',
            warning ? 'text-text-warning' : 'text-text-secondary',
          )}
        >
          {title}
        </span>
      </div>
      {children}
    </section>
  );
}

function DocumentGuidePanel() {
  return (
    <section className='flex w-full shrink-0 flex-col items-start overflow-hidden rounded-[14px] border border-[#fff4c9]'>
      <div className='flex h-9 w-full shrink-0 items-center gap-2 bg-[#fff4c9] px-4 py-[10px]'>
        <IdeaIcon className='size-4 shrink-0 text-[#ffb200]' />
        <span className='typo-b12Strong text-[#ffb200]'>Document Guide</span>
      </div>
      <div className='flex items-center justify-center w-full px-4 py-3 shrink-0 bg-grayscale-gray00'>
        <p className='flex-1 min-w-0 typo-b12 text-text-secondary'>
          {FIGMA_DOCUMENT_GUIDE_TEXT}
        </p>
      </div>
    </section>
  );
}

function ScorePreview({ current, after, gain, tip, showIncrease }) {
  const currentPercent = clampScorePercent(current);
  const gainPercent = showIncrease
    ? Math.max(0, Math.min(100 - currentPercent, toFiniteNumber(gain)))
    : 0;

  return (
    <InfoPanel
      title='Eligibility Score Preview'
      icon={<IdeaIcon className='size-4 text-icon-secondary' />}
    >
      <div className='flex flex-col gap-4 p-4 bg-grayscale-gray00'>
        <div className='flex items-center justify-between'>
          <div className='flex w-[100px] flex-col items-center gap-0.5'>
            <span className='typo-b12 text-text-placeholder'>Current</span>
            <span className='text-[24px] font-semibold leading-[1.2] text-text-primary'>
              {current}
            </span>
          </div>
          <div className='flex flex-col items-center gap-0.5'>
            <NavigationRightIcon className='size-4 text-icon-disabled' />
            {showIncrease && gain > 0 && (
              <span className='typo-b11Strong text-text-success'>
                + {gain} pts
              </span>
            )}
          </div>
          <div className='flex w-[100px] flex-col items-center gap-0.5'>
            <span className='typo-b12 text-text-placeholder'>After Upload</span>
            <span
              className={twMerge(
                'text-[24px] font-semibold leading-[1.2]',
                showIncrease ? 'text-text-success' : 'text-text-placeholder',
              )}
            >
              {showIncrease ? after : '-'}
            </span>
          </div>
          <div className='flex w-[100px] flex-col items-center gap-0.5'>
            <span className='typo-b12 text-text-placeholder'>Max</span>
            <span className='text-[24px] font-semibold leading-[1.2] text-text-placeholder'>
              100
            </span>
          </div>
        </div>
        <div className='relative h-3 overflow-hidden rounded-full bg-grayscale-gray02'>
          <div
            className='h-full rounded-full bg-green-deep03 transition-[width] duration-500'
            style={{ width: `${currentPercent}%` }}
          />
          {gainPercent > 0 && (
            <div
              className='absolute top-0 h-full rounded-full bg-[repeating-linear-gradient(-45deg,rgba(0,158,79,0.2)_0px,rgba(0,158,79,0.2)_4px,#EDFCF7_4px,#EDFCF7_8px)]'
              style={{ left: `${currentPercent}%`, width: `${gainPercent}%` }}
            />
          )}
        </div>
        <p className='typo-b12 text-text-placeholder'>{tip}</p>
      </div>
    </InfoPanel>
  );
}

function FailedUploadWarningModal({ onClose, onContinue }) {
  return (
    <div className='absolute inset-0 z-30 flex items-center justify-center px-4 bg-surface-black40'>
      <section className='flex min-h-[300px] w-[500px] max-w-full flex-col justify-center gap-10 rounded-[30px] bg-grayscale-gray01 px-[60px] py-10'>
        <div className='flex flex-col gap-2'>
          <h3 className='typo-title1 text-text-primary'>
            Some files couldn't be uploaded
          </h3>
          <p className='typo-b16Strong text-text-secondary'>
            Some files couldn't be uploaded. If you continue, they will be
            removed and not saved.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            className='flex items-center justify-center flex-1 h-12 rounded-lg bg-grayscale-gray02 typo-b16Strong text-text-secondary hover:bg-grayscale-gray03'
            onClick={onClose}
          >
            Try again
          </button>
          <button
            type='button'
            className='flex items-center justify-center flex-1 h-12 rounded-lg bg-functional-failed03 typo-b16Strong text-text-inverse'
            onClick={onContinue}
          >
            Continue
          </button>
        </div>
      </section>
    </div>
  );
}

function UnsavedLeaveFeedbackModal({ onStay, onLeave }) {
  return (
    <div className='fixed inset-0 z-[1000000002] flex items-center justify-center bg-surface-black40 px-4'>
      <section
        role='alertdialog'
        aria-modal='true'
        aria-labelledby='evidence-unsaved-title'
        className='flex min-h-[300px] w-[440px] max-w-full flex-col justify-center gap-10 rounded-[30px] bg-grayscale-gray01 px-[60px] py-10 text-center'
      >
        <div className='flex flex-col items-center gap-3'>
          <h3
            id='evidence-unsaved-title'
            className='typo-title1 text-text-primary'
          >
            Leave without saving?
          </h3>
          <p className='typo-b16Strong text-text-secondary'>
            Your uploaded files won't be saved
            <br />
            if you leave this page.
          </p>
        </div>
        <div className='flex items-center gap-3'>
          <button
            type='button'
            className='flex h-12 flex-1 items-center justify-center rounded-lg bg-grayscale-gray02 px-4 typo-b16Strong text-text-secondary hover:bg-grayscale-gray03'
            onClick={onStay}
          >
            Stay on Page
          </button>
          <button
            type='button'
            className='flex h-12 flex-1 items-center justify-center rounded-lg bg-functional-failed03 px-4 typo-b16Strong text-text-inverse hover:bg-functional-failed02'
            onClick={onLeave}
          >
            Leave
          </button>
        </div>
      </section>
    </div>
  );
}

function EvidenceVerifiedSuccessModal({ itemTitle, onClose, onContinue }) {
  const title = itemTitle || 'Evidence';

  return (
    <div className='fixed inset-0 z-[1000000001] flex items-center justify-center bg-surface-black40 px-4'>
      <section
        role='dialog'
        aria-modal='true'
        aria-labelledby='evidence-success-title'
        className='flex min-h-[380px] w-[420px] max-h-[800px] max-w-[760px] flex-col overflow-hidden rounded-[30px] bg-grayscale-gray01'
      >
        <header className='flex items-center justify-between px-[30px] pb-[18px] pt-6'>
          <span className='size-[30px]' aria-hidden='true' />
          <button
            type='button'
            aria-label='Close success modal'
            className='flex size-[30px] items-center justify-center rounded-full bg-grayscale-gray02 text-icon-secondary hover:bg-grayscale-gray03'
            onClick={onClose}
          >
            <CancelIcon className='size-4' />
          </button>
        </header>
        <div className='flex flex-1 flex-col items-center px-[30px] pb-10 text-center'>
          <div className='flex size-[90px] items-center justify-center rounded-full bg-green-light02'>
            <div className='flex size-[60px] items-center justify-center rounded-full bg-grayscale-gray00'>
              <CheckCircleFilledIcon className='size-8 text-icon-success' />
            </div>
          </div>
          <div className='mt-[30px] flex flex-col items-center gap-3'>
            <h3
              id='evidence-success-title'
              className='typo-title1 text-text-primary'
            >
              Your {title} is Verified!
            </h3>
            <p className='typo-b16Strong text-text-secondary'>
              You're on a roll! Keep going and
              <br />
              earn your next 5 points.
            </p>
          </div>
          <button
            type='button'
            className='mt-10 flex h-12 w-60 items-center justify-center rounded-lg bg-green-deep02 px-4 typo-b16Strong text-text-inverse hover:bg-green-deep03'
            onClick={onContinue}
          >
            Earn 5 pts on Koready Skills
          </button>
        </div>
      </section>
    </div>
  );
}

function EvidenceModalPanel({
  title = 'Check Eligibility',
  description = 'Upload your documents to verify your application.',
  items = DEFAULT_EVIDENCE_ITEMS,
  initialScore = 0,
  sessionId,
  onClose,
  onSave,
  registerRequestClose,
  className,
}) {
  const [evidenceItems, setEvidenceItems] = useState(items);
  const [activeId, setActiveId] = useState(null);
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [isUnsavedLeaveOpen, setIsUnsavedLeaveOpen] = useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState('back');
  const [saveState, setSaveState] = useState('idle');
  const [scorePreview, setScorePreview] = useState(null);
  const [successItemTitle, setSuccessItemTitle] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setEvidenceItems((prev) => {
      const prevMap = new Map(prev.map((item) => [item.id, item]));
      return (items || []).map((newItem) => {
        const existing = prevMap.get(newItem.id);
        if (!existing) return newItem;
        const hasLocalLinks =
          Array.isArray(existing.links) && existing.links.length > 0;
        return {
          ...newItem,
          files: mergeEvidenceFiles(existing.files ?? [], newItem.files ?? []),
          links: hasLocalLinks ? existing.links : (newItem.links ?? []),
          fileName: existing.fileName || newItem.fileName,
        };
      });
    });
  }, [items]);

  const activeItem = evidenceItems.find((item) => item.id === activeId);
  const originalActiveItem = activeItem
    ? (items || []).find((item) => item.id === activeItem.id)
    : null;
  const isDetailOpen = Boolean(activeItem);
  const totalCount = evidenceItems.length;
  const uploadedCount = evidenceItems.filter(isEvidenceUploaded).length;
  const progress = totalCount > 0 ? (uploadedCount / totalCount) * 100 : 0;
  // PM 정책: 점수는 소수점 1자리 유지 (정수 반올림 금지)
  const roundScore1 = (value) => Math.round(value * 10) / 10;
  const currentScore = roundScore1(
    Math.min(100, clampScorePercent(initialScore)),
  );
  const activeFiles = normalizeFiles(activeItem || {});
  const activeLinks = normalizeLinks(activeItem || {});
  const sortedFiles = [...activeFiles].sort((a, b) => {
    const statusDiff =
      (STATUS_PRIORITY[b.status] || 0) - (STATUS_PRIORITY[a.status] || 0);
    return statusDiff || (b.createdAt || 0) - (a.createdAt || 0);
  });
  const hasUploadingFiles = activeFiles.some(
    (file) => file.status === 'uploading',
  );
  const hasFailedFiles = activeFiles.some((file) => file.status === 'failed');
  const activeUploaded = activeItem ? isEvidenceUploaded(activeItem) : false;
  const hasSuccessfulActiveFile = activeFiles.some(
    (file) => file.status === 'success',
  );
  const hasValidActiveLinks = hasValidLinks(activeLinks);
  const shouldShowScoreIncrease =
    activeUploaded ||
    hasUploadingFiles ||
    hasSuccessfulActiveFile ||
    hasValidActiveLinks;
  const scoreGain = activeItem && !activeUploaded ? activeItem.points || 0 : 0;
  const scoreAfter = Math.min(100, currentScore + scoreGain);
  const scoreTip = activeUploaded
    ? 'Already uploaded - counted in your current score.'
    : 'Upload a file to preview how your score changes.';
  const previewCurrent = scorePreview
    ? roundScore1(toFiniteNumber(scorePreview.current, currentScore))
    : currentScore;
  const previewAfter = scorePreview
    ? roundScore1(toFiniteNumber(scorePreview.afterUpload, scoreAfter))
    : scoreAfter;
  const previewGain = scorePreview
    ? roundScore1(toFiniteNumber(scorePreview.gain, scoreGain))
    : scoreGain;
  const previewTip = scorePreview
    ? `Uploading this document will add ${previewGain} points, bringing your score to ${previewAfter}/100.`
    : scoreTip;
  const scorePreviewDependency = activeFiles
    .map(
      (file) =>
        `${file.id}:${file.status}:${file.key || ''}:${file.persistedId || ''}`,
    )
    .join('|');
  const activeLinksDependency = activeLinks
    .map((link) => `${link.id}:${link.linkType}:${link.linkUrl || ''}`)
    .join('|');
  const activeFilesSignature = getFilesSignature(activeFiles);
  const originalFilesSignature = getFilesSignature(
    normalizeFiles(originalActiveItem || {}),
  );
  const activeLinksSignature = getLinksSignature(activeLinks);
  const originalLinksSignature = getLinksSignature(
    normalizeLinks(originalActiveItem || {}),
  );
  const hasUnsavedActiveChanges =
    Boolean(activeItem) &&
    (activeFilesSignature !== originalFilesSignature ||
      activeLinksSignature !== originalLinksSignature);

  useEffect(() => {
    if (!sessionId || !activeItem?.variableName) {
      setScorePreview(null);
      return undefined;
    }

    const hasPreviewEvidence =
      activeUploaded ||
      hasUploadingFiles ||
      hasSuccessfulActiveFile ||
      hasValidActiveLinks;

    if (!hasPreviewEvidence) {
      setScorePreview(null);
      return undefined;
    }

    let alive = true;
    getPathfinderEvidenceScorePreview(sessionId, activeItem.variableName)
      .then((preview) => {
        if (alive) setScorePreview(preview);
      })
      .catch(() => {
        if (alive) setScorePreview(null);
      });

    return () => {
      alive = false;
    };
  }, [
    activeId,
    activeItem?.variableName,
    activeUploaded,
    activeLinksDependency,
    hasUploadingFiles,
    hasSuccessfulActiveFile,
    hasValidActiveLinks,
    scorePreviewDependency,
    sessionId,
  ]);

  const updateActiveItem = (updater) => {
    setEvidenceItems((prev) =>
      prev.map((item) =>
        item.id === activeId ? { ...item, ...updater(item) } : item,
      ),
    );
  };

  const uploadFile = async (file, id) => {
    if (!sessionId) {
      window.setTimeout(() => {
        updateActiveItem((item) => ({
          files: normalizeFiles(item).map((fileItem) =>
            fileItem.id === id
              ? {
                  ...fileItem,
                  status: 'success',
                  progress: 100,
                  loaded: fileItem.file.size,
                }
              : fileItem,
          ),
        }));
      }, 650);
      return;
    }

    try {
      const { uploadUrl, key } = await getPathfinderEvidenceUploadUrl(
        sessionId,
        file.name,
      );
      await uploadToS3(file, uploadUrl, ({ percent, loaded, total }) => {
        updateActiveItem((item) => ({
          files: normalizeFiles(item).map((fileItem) =>
            fileItem.id === id
              ? { ...fileItem, progress: percent, loaded, total }
              : fileItem,
          ),
        }));
      });

      updateActiveItem((item) => ({
        files: normalizeFiles(item).map((fileItem) =>
          fileItem.id === id
            ? { ...fileItem, status: 'success', progress: 100, key }
            : fileItem,
        ),
      }));
    } catch (error) {
      updateActiveItem((item) => ({
        files: normalizeFiles(item).map((fileItem) =>
          fileItem.id === id
            ? { ...fileItem, status: 'failed', errorType: 'network' }
            : fileItem,
        ),
      }));
    }
  };

  const addFiles = (files) => {
    const fileItems = files.map((file) => ({
      id: Date.now() + Math.random(),
      file,
      status: file.size > MAX_FILE_SIZE ? 'failed' : 'uploading',
      progress: file.size > MAX_FILE_SIZE ? undefined : 0,
      loaded: 0,
      errorType: file.size > MAX_FILE_SIZE ? 'size' : null,
      createdAt: Date.now(),
    }));

    updateActiveItem((item) => ({
      files: [...normalizeFiles(item), ...fileItems],
    }));

    fileItems
      .filter((item) => item.status === 'uploading')
      .forEach((item) => uploadFile(item.file, item.id));
  };

  const retryFile = (fileId) => {
    const target = activeFiles.find((file) => file.id === fileId);
    if (!target) return;

    updateActiveItem((item) => ({
      files: normalizeFiles(item).map((fileItem) =>
        fileItem.id === fileId
          ? {
              ...fileItem,
              status: 'uploading',
              progress: 0,
              loaded: 0,
              errorType: null,
            }
          : fileItem,
      ),
    }));
    uploadFile(target.file, fileId);
  };

  const removeFile = (fileId) => {
    updateActiveItem((item) => ({
      files: normalizeFiles(item).filter((file) => file.id !== fileId),
    }));
  };

  const setActiveLinks = (setter) => {
    updateActiveItem((item) => ({
      links:
        typeof setter === 'function' ? setter(normalizeLinks(item)) : setter,
    }));
  };

  const closeDetail = () => {
    setIsWarningOpen(false);
    setIsDragActive(false);
    setActiveId(null);
  };

  const requestDetailBack = () => {
    if (hasUnsavedActiveChanges) {
      setPendingLeaveAction('back');
      setIsUnsavedLeaveOpen(true);
      return;
    }

    closeDetail();
  };

  const requestModalClose = () => {
    if (hasUnsavedActiveChanges) {
      setPendingLeaveAction('close');
      setIsUnsavedLeaveOpen(true);
      return;
    }

    onClose?.();
  };

  const stayOnUnsavedPage = () => {
    setIsUnsavedLeaveOpen(false);
    setPendingLeaveAction('back');
  };

  const leaveUnsavedPage = () => {
    setIsUnsavedLeaveOpen(false);
    if (pendingLeaveAction === 'close') {
      onClose?.();
      return;
    }

    closeDetail();
    setPendingLeaveAction('back');
  };

  useEffect(() => {
    if (!registerRequestClose) return undefined;
    registerRequestClose(requestModalClose);
    return () => registerRequestClose(onClose);
  });

  const persistActiveItem = async (removeFailed = false) => {
    if (!activeItem) return;
    const nextFiles = removeFailed
      ? activeFiles.filter((file) => file.status !== 'failed')
      : activeFiles;
    const nextLinks = activeLinks.filter(
      (link) => link.linkUrl?.trim() || link.linkType !== 'Link',
    );
    const nextItem = {
      ...activeItem,
      files: nextFiles,
      links: nextLinks,
      fileName: nextFiles.find((file) => file.status === 'success')?.file?.name,
      state:
        hasSuccessfulFiles(nextFiles) || hasValidLinks(nextLinks)
          ? 'uploaded'
          : 'default',
    };

    setSaveState('loading');
    try {
      await new Promise((resolve) => window.setTimeout(resolve, SAVE_DELAY_MS));
      await onSave?.(nextItem);

      setEvidenceItems((prev) =>
        prev.map((item) => (item.id === activeId ? nextItem : item)),
      );
      setSuccessItemTitle(nextItem.title);
      closeDetail();
    } finally {
      setSaveState('idle');
    }
  };

  const handleSave = () => {
    if (saveState === 'loading') return;
    if (hasUploadingFiles) {
      return;
    }
    if (hasFailedFiles) {
      setIsWarningOpen(true);
      return;
    }
    persistActiveItem();
  };

  const ensureInitialLinkRows = () => {
    if (activeLinks.length === 0) setActiveLinks([makeLinkRow()]);
  };

  const detailContent = activeItem ? (
    <div
      className={twMerge(
        'absolute inset-0 z-10 flex flex-col rounded-[30px] bg-grayscale-gray00 transition-transform duration-300 ease-out',
        isDetailOpen ? 'translate-x-0' : 'translate-x-full',
      )}
    >
      <header className='flex shrink-0 items-start gap-6 border-b border-grayscale-gray01 bg-grayscale-gray00 px-[30px] py-6'>
        <div className='flex items-start flex-1 min-w-0 gap-2'>
          <button
            type='button'
            className='flex items-center justify-center rounded-lg size-8 shrink-0 hover:bg-grayscale-gray01'
            onClick={requestDetailBack}
          >
            <ArrowLeftIcon className='size-5 text-icon-secondary' />
          </button>
          <div className='flex-1 min-w-0'>
            <div className='flex items-center min-w-0 gap-2'>
              <h3 className='truncate typo-b16Strong text-text-primary'>
                {activeItem.title}
              </h3>
              <StatusPill status={activeItem.status} />
            </div>
            <p className='typo-b13 text-text-placeholder'>
              {activeItem.category} Document
            </p>
          </div>
        </div>
        <CloseButton
          type='light'
          size='size-[30px]'
          onClick={requestModalClose}
        />
      </header>

      <main className='flex min-h-0 flex-1 flex-col gap-[30px] overflow-y-auto px-[30px] pb-6 pt-4 [scrollbar-color:#b1ada8_#f9f7f4] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-[10px] [&::-webkit-scrollbar-thumb]:bg-grayscale-gray04 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-[6px]'>
        <DocumentGuidePanel />

        <section className='flex flex-col w-full gap-2'>
          <p className='typo-b12Strong uppercase tracking-[0.04em] text-text-placeholder'>
            Upload file
          </p>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='typo-b12 text-text-placeholder'>
              Accepted Formats:
            </span>
            {['PDF', 'JPG / PNG', 'Max 50 MB', activeItem.category].map(
              (chip) => (
                <span
                  key={chip}
                  className={twMerge(
                    'inline-flex h-6 items-center rounded-lg px-2 typo-b12Strong',
                    chip === activeItem.category
                      ? 'bg-green-light01 text-text-success'
                      : 'bg-grayscale-gray02 text-text-secondary',
                  )}
                >
                  {chip}
                </span>
              ),
            )}
          </div>
          <div
            className={twMerge(
              'group flex h-[140px] w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-[1.6px] border-dashed border-grayscale-gray03 transition-colors',
              'hover:border-green-deep02 hover:bg-green-light01 active:border-green-deep03',
              isDragActive && 'border-green-deep02 bg-green-light01',
            )}
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDragLeave={() => setIsDragActive(false)}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              addFiles(Array.from(event.dataTransfer.files || []));
            }}
          >
            <input
              ref={fileInputRef}
              type='file'
              accept='.pdf,.jpg,.jpeg,.png'
              multiple
              className='hidden'
              onChange={(event) => {
                addFiles(Array.from(event.target.files || []));
                event.target.value = '';
              }}
            />
            <span className='flex h-[38px] items-center gap-1 rounded-lg bg-grayscale-gray01 px-3 typo-b14Strong text-text-secondary group-hover:bg-green-light01 group-hover:text-text-success'>
              <UploadIcon className='size-4' />
              Upload
            </span>
            <p className='typo-b14 text-text-disabled'>
              Drag & drop or Browse to choose your file
            </p>
          </div>

          {sortedFiles.length > 0 && (
            <div className='flex flex-col w-full gap-2 mt-2'>
              {sortedFiles.map((fileData) => (
                <FileUploadRow
                  key={fileData.id}
                  fileData={fileData}
                  onDelete={() => removeFile(fileData.id)}
                  onRetry={() => retryFile(fileData.id)}
                />
              ))}
            </div>
          )}
        </section>

        {activeItem.allowLinks && (
          <section
            className='flex flex-col w-full gap-2'
            onFocus={ensureInitialLinkRows}
          >
            <p className='typo-b12Strong uppercase tracking-[0.04em] text-text-placeholder'>
              Add a link
            </p>
            <LinkRows
              links={activeLinks.length > 0 ? activeLinks : [makeLinkRow()]}
              onChange={setActiveLinks}
            />
          </section>
        )}

        <ScorePreview
          current={previewCurrent}
          after={previewAfter}
          gain={previewGain}
          tip={previewTip}
          showIncrease={shouldShowScoreIncrease}
        />
        <div className='h-2 shrink-0' />
      </main>

      <footer className='flex shrink-0 items-center justify-between border-t border-grayscale-gray01 bg-grayscale-gray00 px-[30px] pb-6 pt-4'>
        <button
          type='button'
          className='flex items-center justify-center h-12 px-4 rounded-lg bg-grayscale-gray01 typo-b16Strong text-text-secondary hover:bg-grayscale-gray02'
          onClick={requestDetailBack}
        >
          Back
        </button>
        <button
          type='button'
          className='flex h-12 w-60 items-center justify-center gap-1.5 rounded-lg bg-green-deep02 px-4 typo-b16Strong text-text-inverse hover:bg-green-deep03 active:scale-[0.98]'
          onClick={handleSave}
        >
          {saveState === 'loading' ? (
            <DotsLoader />
          ) : (
            <>
              <CheckCircleIcon className='size-5' />
              Save and Update Score
            </>
          )}
        </button>
      </footer>

      {isWarningOpen && (
        <FailedUploadWarningModal
          onClose={() => setIsWarningOpen(false)}
          onContinue={() => {
            setIsWarningOpen(false);
            persistActiveItem(true);
          }}
        />
      )}
    </div>
  ) : null;

  return (
    <>
      <section
        role='dialog'
        aria-modal='true'
        aria-labelledby='evidence-modal-title'
        className={twMerge(
          'relative flex h-[600px] max-h-[600px] min-h-[440px] w-[576px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[30px] bg-grayscale-gray00 shadow-[0_24px_64px_rgba(23,20,16,0.28)]',
          className,
        )}
      >
        <header className='flex w-full shrink-0 items-start gap-6 border-b border-grayscale-gray01 bg-grayscale-gray00 px-[30px] py-6'>
          <div className='flex-1 min-w-0'>
            <h2
              id='evidence-modal-title'
              className='typo-title3 text-text-primary'
            >
              {title}
            </h2>
            <p className='mt-1 typo-b13 text-text-placeholder'>{description}</p>
          </div>
          <CloseButton
            type='light'
            size='size-[30px]'
            onClick={requestModalClose}
          />
        </header>

        <main className='flex min-h-0 flex-1 flex-col gap-[30px] overflow-y-auto px-[30px] pb-[120px] pt-[30px] [scrollbar-color:#b1ada8_#f9f7f4] [scrollbar-width:thin] [&::-webkit-scrollbar-thumb]:rounded-[10px] [&::-webkit-scrollbar-thumb]:bg-grayscale-gray04 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar]:w-[6px]'>
          <div className='flex flex-col w-full'>
            <div className='flex items-center justify-between w-full px-1 pb-2 typo-b14Strong'>
              <span className='text-text-secondary'>Upload progress</span>
              <span className='text-text-success'>
                {uploadedCount} / {totalCount} updated
              </span>
            </div>
            <div className='w-full h-2 overflow-hidden rounded-full bg-grayscale-gray02'>
              <div
                className='h-full rounded-full bg-gradient-to-r from-green-deep03 via-green-deep02 to-green-deep03 transition-[width] duration-500'
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          {evidenceItems.length > 0 ? (
            <div className='flex flex-col w-full gap-3'>
              {evidenceItems.map((item) => (
                <EvidenceUploadCard
                  key={item.id || item.title}
                  {...item}
                  state={isEvidenceUploaded(item) ? 'uploaded' : 'default'}
                  fileName={
                    item.fileName ||
                    normalizeFiles(item).find(
                      (file) => file.status === 'success',
                    )?.file?.name
                  }
                  linkUrl={
                    normalizeLinks(item).find((link) => link.linkUrl?.trim())
                      ?.linkUrl
                  }
                  linkCount={
                    normalizeLinks(item).filter((link) => link.linkUrl?.trim())
                      .length
                  }
                  onAction={() => {
                    setActiveId(item.id);
                    if (item.allowLinks && normalizeLinks(item).length === 0) {
                      setEvidenceItems((prev) =>
                        prev.map((evidence) =>
                          evidence.id === item.id
                            ? { ...evidence, links: [makeLinkRow()] }
                            : evidence,
                        ),
                      );
                    }
                  }}
                />
              ))}
            </div>
          ) : (
            <div className='flex min-h-[180px] w-full items-center justify-center rounded-[15px] border border-grayscale-gray02 bg-grayscale-gray00 p-6 text-center typo-b14 text-text-placeholder'>
              No evidence is required based on your Pathfinder answers yet.
            </div>
          )}
        </main>

        {detailContent}
      </section>
      {isUnsavedLeaveOpen && (
        <UnsavedLeaveFeedbackModal
          onStay={stayOnUnsavedPage}
          onLeave={leaveUnsavedPage}
        />
      )}
      {successItemTitle && (
        <EvidenceVerifiedSuccessModal
          itemTitle={successItemTitle}
          onClose={() => setSuccessItemTitle('')}
          onContinue={() => setSuccessItemTitle('')}
        />
      )}
    </>
  );
}

function EvidenceModal({
  isOpen = true,
  withOverlay = true,
  onClose,
  ...props
}) {
  const requestCloseRef = useRef(onClose);
  requestCloseRef.current = onClose;

  if (!isOpen) return null;

  const registerRequestClose = (handler) => {
    requestCloseRef.current = handler || onClose;
  };

  const modal = (
    <EvidenceModalPanel
      {...props}
      onClose={onClose}
      registerRequestClose={registerRequestClose}
    />
  );

  if (!withOverlay) return modal;

  return ReactDOM.createPortal(
    <div className='fixed inset-0 z-[1000000000] flex items-center justify-center bg-surface-black40 p-4 backdrop-blur-[4px]'>
      <button
        type='button'
        aria-label='Close evidence modal'
        className='absolute inset-0 cursor-default'
        onClick={() => requestCloseRef.current?.()}
      />
      <div className='relative z-10'>{modal}</div>
    </div>,
    document.body,
  );
}

export {
  EvidenceModalPanel,
  DEFAULT_EVIDENCE_ITEMS,
  getRequestedEvidenceItems,
  getRequestedEvidenceItemsFromPathfinderDraft,
  buildDraftFromSessionData,
  getRequestedEvidenceItemsWithFallback,
};
export default EvidenceModal;
